import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Wallet, Plus } from 'lucide-react';

import API_URL from '../utils/api';

const Loans = () => {
    const [loans, setLoans] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        employee_id: '',
        amount: '',
        monthly_deduction: '',
        reason: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [loansRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/loans`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setLoans(loansRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                monthly_deduction: parseFloat(formData.monthly_deduction)
            };

            await axios.post(`${API_URL}/api/loans`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ employee_id: '', amount: '', monthly_deduction: '', reason: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating loan');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading loans...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="loans-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Employee Loans
                    </h1>
                    <p className="text-slate-500 mt-1">Manage employee loan requests</p>
                </div>
                
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-loan-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Loan
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Create Employee Loan
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Apply for a loan with amount, tenure and reason.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee</Label>
                                <Select value={formData.employee_id} onValueChange={(v) => setFormData({...formData, employee_id: v})}>
                                    <SelectTrigger className="rounded-none mt-1" data-testid="loan-employee-select">
                                        <SelectValue placeholder="Select employee" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-none">
                                        {employees.map(emp => (
                                            <SelectItem key={emp._id} value={emp._id}>
                                                {emp.name} ({emp.employee_id})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Loan Amount</Label>
                                <Input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                    className="rounded-none mt-1"
                                    required
                                    data-testid="loan-amount-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Deduction</Label>
                                <Input
                                    type="number"
                                    value={formData.monthly_deduction}
                                    onChange={(e) => setFormData({...formData, monthly_deduction: e.target.value})}
                                    className="rounded-none mt-1"
                                    required
                                    data-testid="loan-monthly-deduction-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason</Label>
                                <Textarea
                                    value={formData.reason}
                                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                    className="rounded-none mt-1"
                                    rows={3}
                                    required
                                    data-testid="loan-reason-input"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="loan-save-btn">
                                    Create Loan
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Loans Table */}
            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Wallet className="h-5 w-5 text-brand" />
                        Active Loans
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="loans-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Total Amount</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Remaining</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Monthly Deduction</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Reason</th>
                                    <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loans.map((loan) => (
                                    <tr key={loan._id} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="p-4 font-medium text-slate-900">{loan.employee_name}</td>
                                        <td className="p-4 text-right text-slate-700">${loan.amount?.toLocaleString()}</td>
                                        <td className="p-4 text-right font-medium text-red-600">${loan.remaining_amount?.toLocaleString()}</td>
                                        <td className="p-4 text-right text-slate-700">${loan.monthly_deduction?.toLocaleString()}</td>
                                        <td className="p-4 text-slate-700 max-w-xs truncate">{loan.reason}</td>
                                        <td className="p-4 text-center">
                                            <Badge className={`rounded-none ${loan.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-slate-100 text-slate-800 border-slate-200'}`}>
                                                {loan.status}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                                {loans.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-slate-500">
                                            No loans found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Loans;
